import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Ruler } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";

const ACRE = 4046.86;
const num = (n) => Math.round(n).toLocaleString();

const UNITS = [
  ["m2", "square metres", 1],
  ["acres", "acres", ACRE],
  ["ha", "hectares", 10000],
];

/** The generic sizing list: what each format of the game needs in land, and what it costs to build. */
export default function PitchSizing() {
  const [data, setData] = useState(null);
  const [county, setCounty] = useState("");
  const [counties, setCounties] = useState([]);
  const [size, setSize] = useState("8707");
  const [unit, setUnit] = useState("m2");

  useSeo({
    title: "Pitch Sizing And Land Calculator — KickStartKenyaFootball",
    description:
      "How much land a futsal court, a 5-a-side, a 7-a-side, a 9-a-side and an 11-a-side pitch actually need, using the published standards of England, France, Portugal and Brazil — with build cost bands and a land calculator.",
  });

  useEffect(() => {
    api.get(`/pitch-sizing${county ? `?county=${encodeURIComponent(county)}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [county]);

  useEffect(() => {
    api.get("/counties")
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : r.data.counties || [];
        setCounties(rows.map((c) => c.county || c.name).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  const plot = useMemo(() => {
    const factor = UNITS.find((u) => u[0] === unit)?.[2] || 1;
    return Math.max(0, parseFloat(size || "0") * factor);
  }, [size, unit]);

  if (!data) return <div className="min-h-[60vh] bg-black" data-testid="pitch-sizing-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="pitch-sizing-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Sizing and costing</p>
          <h1 className="display-1 mt-5 max-w-4xl">How Much Land Does A Pitch Need</h1>
          <p className="body-copy mt-6 max-w-3xl">
            One generic list, so anyone with a plot can work out what fits on it before spending a shilling. The
            playing dimensions are the published federation standards of England, France, Portugal and Brazil —
            four countries that build grassroots football properly. The land arithmetic and the money are ours.
          </p>
          <div className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <Ruler size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span>
              <VerifyChip v="estimate" /> Cost bands are our own estimates published to be argued with. They are
              not quotations, tenders or budgets. Land excluded.
            </span>
          </div>
        </Reveal>

        <section className="mt-14" data-testid="pitch-calculator">
          <p className="eyebrow">Your plot</p>
          <h2 className="display-3 mt-4">What Fits On The Land You Have</h2>
          <div className="mt-7 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Plot size</span>
              <input
                value={size}
                onChange={(e) => setSize(e.target.value.replace(/[^\d.]/g, ""))}
                inputMode="decimal"
                data-testid="plot-size-input"
                className="mt-2 w-40 border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Measured in</span>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                data-testid="plot-unit-select"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              >
                {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">County prices</span>
              <select
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                data-testid="plot-county-select"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
              >
                <option value="">National average</option>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            {data.cost_index !== 1 ? (
              <p className="mono pb-3 text-[0.55rem] uppercase tracking-[0.16em] text-ochre" data-testid="cost-index-note">
                Cost index {data.cost_index}x
              </p>
            ) : null}
          </div>

          <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3" data-testid="fit-results">
            {data.formats.map((f) => {
              const fits = plot > 0 ? Math.floor(plot / f.land_needed_m2) : 0;
              return (
                <div key={f.id} className="bg-[#0b0b0b] p-6" data-testid={`fit-${f.id}`}>
                  <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">{f.name}</p>
                  <p className="mt-3 font-display text-4xl font-bold leading-none text-white">
                    {fits}
                    <span className="ml-2 font-body text-sm font-normal text-zinc-500">
                      {fits === 1 ? "pitch" : "pitches"}
                    </span>
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-zinc-400">
                    {num(f.land_needed_m2)} m² each ({f.land_needed_acres} acres). {fits > 0
                      ? `Build cost $${num(f.cost_mid_usd * fits)} at the mid band.`
                      : "Not enough land for one."}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-20" data-testid="pitch-table-section">
          <p className="eyebrow">The generic list</p>
          <h2 className="display-3 mt-4">Every Format, Sized And Costed</h2>
          <div className="mt-8 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[1000px] text-left" data-testid="pitch-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Format", "Playing size", "Run-off", "Fenced footprint", "Land needed", "Per hectare", "Build cost band"].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.formats.map((f) => (
                  <tr key={f.id} className="border-b border-zinc-900 align-top" data-testid={`pitch-row-${f.id}`}>
                    <td className="px-4 py-5">
                      <span className="block font-display text-base uppercase leading-tight text-white">{f.name}</span>
                      <span className="mono mt-1 block text-[0.5rem] uppercase tracking-[0.14em] text-zinc-500">{f.players}</span>
                    </td>
                    <td className="mono px-4 py-5 text-xs text-zinc-300">
                      {f.length} x {f.width} m
                      <span className="mt-1 block text-[0.5rem] tracking-[0.14em] text-zinc-600">
                        range {f.min} to {f.max}
                      </span>
                    </td>
                    <td className="mono px-4 py-5 text-xs text-zinc-300">{f.run_off} m</td>
                    <td className="mono px-4 py-5 text-xs text-zinc-300">
                      {f.fenced_length_m} x {f.fenced_width_m} m
                      <span className="mt-1 block text-[0.5rem] tracking-[0.14em] text-zinc-600">{num(f.fenced_area_m2)} m²</span>
                    </td>
                    <td className="mono px-4 py-5 text-xs text-white">
                      {num(f.land_needed_m2)} m²
                      <span className="mt-1 block text-[0.5rem] tracking-[0.14em] text-zinc-500">{f.land_needed_acres} acres</span>
                    </td>
                    <td className="mono px-4 py-5 text-xs text-zinc-300">{f.pitches_per_hectare}</td>
                    <td className="mono px-4 py-5 text-xs text-ochre">
                      ${num(f.cost_low_usd)} – ${num(f.cost_high_usd)}
                      <span className="mt-1 block text-[0.5rem] tracking-[0.14em] text-zinc-500">
                        mid ${num(f.cost_mid_usd)} · KSh {num(f.cost_mid_ksh)}
                      </span>
                      <span className="mt-1 block text-[0.5rem] normal-case tracking-normal text-zinc-600">{f.cost_note}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-20" data-testid="pitch-standards">
          <p className="eyebrow">Where the dimensions come from</p>
          <h2 className="display-3 mt-4">Four Countries That Build It Properly</h2>
          <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2">
            {data.countries.map((c) => (
              <div key={c.country} className="bg-[#0b0b0b] p-6 md:p-7" data-testid={`standard-${c.country.toLowerCase()}`}>
                <h3 className="font-display text-lg uppercase tracking-tight text-white">{c.country}</h3>
                <p className="mono mt-2 text-[0.5rem] uppercase tracking-[0.16em] text-ochre">{c.body}</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{c.lesson}</p>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`standard-link-${c.country.toLowerCase()}`}
                  className="mono mt-4 inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
                >
                  Their guidance <ArrowUpRight size={11} />
                </a>
              </div>
            ))}
          </div>

          <div className="mt-10 grid gap-px bg-zinc-800 lg:grid-cols-2">
            {data.formats.map((f) => (
              <div key={f.id} className="bg-black p-6" data-testid={`format-standards-${f.id}`}>
                <h3 className="font-display text-base uppercase tracking-tight text-white">{f.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.why}</p>
                <p className="mono mt-3 text-[0.5rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-600">
                  Surface: {f.surface}
                </p>
                <dl className="mt-4 space-y-2 border-t border-zinc-900 pt-4">
                  {f.standards.map(([country, line]) => (
                    <div key={country} className="text-xs leading-relaxed">
                      <dt className="mono inline text-[0.5rem] uppercase tracking-[0.16em] text-ochre">{country}: </dt>
                      <dd className="inline text-zinc-400">{line}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20" data-testid="pitch-build-lines">
          <p className="eyebrow">What you are actually buying</p>
          <h2 className="display-3 mt-4">The Ten Lines Of A Pitch Build</h2>
          <div className="mt-8 border-t border-white/20">
            {data.build_lines.map((b, i) => (
              <div key={b.line} className="grid grid-cols-[36px_1fr] gap-4 border-b border-white/10 py-4 md:grid-cols-[56px_220px_1fr] md:gap-8">
                <span className="mono text-sm text-maasai">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-base uppercase leading-tight tracking-tight text-white">{b.line}</span>
                <span className="col-span-2 text-sm leading-relaxed text-zinc-400 md:col-span-1">{b.note}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20" data-testid="pitch-method">
          <p className="eyebrow">Method and limits</p>
          <h2 className="display-3 mt-4">How These Numbers Are Worked Out</h2>
          <ul className="mt-7 max-w-4xl space-y-4">
            {data.method.map((m) => (
              <li key={m} className="border-l-2 border-zinc-800 pl-5 text-sm leading-relaxed text-zinc-300">{m}</li>
            ))}
          </ul>
          <p className="mt-8 max-w-4xl border-l-2 border-ochre bg-[#0b0b0b] p-6 text-sm leading-relaxed text-zinc-300" data-testid="pitch-worked-example">
            {data.worked_example}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/apply" className="btn-primary" data-testid="pitch-apply-cta">Know a plot this fits? File it</Link>
            <Link to="/court-rollout" className="btn-ghost">The county court rollout</Link>
            <Link to="/facilities" className="btn-ghost">Grassroots ecosystem</Link>
            <Link to="/sources" className="btn-ghost">Sources and method</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
